/**
 * Avatar background colour, generated per user in code (DiceBear just renders
 * whatever hex we pass). Two jobs: (1) contrast — the black-and-white face is
 * nearly invisible on the dark theme without a backdrop; (2) recognition —
 * colour is the cue that survives at small feed sizes.
 *
 * Rather than pick from a fixed list (which forces identical repeats when users
 * collide), each seed is placed on a CONTINUOUS hue wheel and given one of a few
 * lightness levels, so users get a near-unique colour + shade. Two hue bands are
 * excluded — orange (~20-50) and teal (~158-192) — so a chip never collides with
 * the stance rings (OPPOSE ≈ orange, SUPPORT ≈ teal); that cut also skips the
 * skin-tone hue region. Saturation is high (vivid, on-brand) and the lightness
 * levels keep the black face legible (>= 4.5:1) on every hue.
 *
 * This makes clashes rare, not impossible: each avatar is coloured from its own
 * seed with no knowledge of the others, so two users can still occasionally land
 * on a similar colour (the face is then the tie-breaker). Guaranteeing all
 * users distinct would require assigning colours centrally at creation time.
 */
const AVATAR_HUE_SEGMENTS = [[0, 20], [50, 158], [192, 360]]; // wheel minus orange & teal
const AVATAR_HUE_TOTAL = AVATAR_HUE_SEGMENTS.reduce((sum, [a, b]) => sum + (b - a), 0);
const AVATAR_SATURATION = 72;
const AVATAR_LIGHTNESS_LEVELS = [66, 72, 78]; // all keep black legible on every hue

// FNV-1a + a mixing finalizer. `salt` yields two independent draws (hue, shade).
const hashSeed = (seed, salt) => {
  let h = (2166136261 ^ salt) >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  h ^= h >>> 13;
  return h >>> 0;
};

const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `${f(0)}${f(8)}${f(4)}`; // no leading '#'
};

// Deterministic seed -> vivid, ring-safe, black-legible background hex.
const seedBgColor = (seed) => {
  let x = hashSeed(seed, 0) % AVATAR_HUE_TOTAL;
  let hue = 0;
  for (const [a, b] of AVATAR_HUE_SEGMENTS) {
    if (x < b - a) { hue = a + x; break; }
    x -= b - a;
  }
  const l =
    AVATAR_LIGHTNESS_LEVELS[hashSeed(seed, 0x9e3779b9) % AVATAR_LIGHTNESS_LEVELS.length];
  return hslToHex(hue, AVATAR_SATURATION, l);
};

/**
 * Get profile picture URL for a user.
 *
 * Uses DiceBear "notionists-neutral" — a bold, frame-filling black-and-white
 * face (no hair/body) on a seeded light background colour. At feed size the few
 * BIG features (brows, mouth, glasses) stay legible where the full figure's
 * finer detail would not, and the background colour is the primary recognition
 * cue. The monochrome face carries NO skin tone, so an avatar never embodies a
 * race even when agents discuss identity-related topics (e.g. bias in AI
 * proctoring). Each seed is distinct.
 *
 * @param {string} username - The user's username (fallback seed)
 * @param {string} [avatarSeed] - Custom seed chosen by the user
 * @returns {string} URL to the avatar SVG
 */
export const getProfilePicture = (username, avatarSeed) => {
  const seed = avatarSeed || username || 'default';
  const bg = seedBgColor(seed);
  return `https://api.dicebear.com/9.x/notionists-neutral/svg?seed=${encodeURIComponent(
    seed
  )}&backgroundColor=${bg}&backgroundType=solid`;
};

/**
 * Curated avatar seeds for the avatar picker during onboarding.
 * Each produces a distinct black-and-white notionists-neutral face.
 */
export const AVATAR_OPTIONS = [
  { seed: "pick_alpha", label: "Style 1" },
  { seed: "pick_bravo", label: "Style 2" },
  { seed: "pick_charlie", label: "Style 3" },
  { seed: "pick_delta", label: "Style 4" },
  { seed: "pick_echo", label: "Style 5" },
  { seed: "pick_foxtrot", label: "Style 6" },
  { seed: "pick_golf", label: "Style 7" },
  { seed: "pick_hotel", label: "Style 8" },
  { seed: "pick_india", label: "Style 9" },
  { seed: "pick_juliet", label: "Style 10" },
  { seed: "pick_kilo", label: "Style 11" },
  { seed: "pick_lima", label: "Style 12" },
  { seed: "pick_mike", label: "Style 13" },
  { seed: "pick_november", label: "Style 14" },
  { seed: "pick_oscar", label: "Style 15" },
  { seed: "pick_papa", label: "Style 16" },
];
